import React, { useState, useEffect, useMemo } from "react";
import { Creator, BookingRecord, Voucher } from "../types";
import { login, getStoredUser } from "../services/api/auth";
import { getPartnerDashboard } from "../services/api/partners";
import {
  getPartnerWithdrawals,
  getPartnerBalance,
  requestWithdrawal,
  Withdrawal,
  WithdrawalBalance,
} from "../services/api/partnerWithdrawals";
import {
  getPartnerProfile,
  updatePartnerProfile,
  requestEmailChange,
  verifyEmailChange,
  cancelEmailChange,
  PartnerProfile,
  UpdateProfilePayload,
} from "../services/api/partnerProfile";
import {
  subscribeToUserNotifications,
  NotificationEventData,
} from "../services/api/realtime";
import {
  LayoutDashboard,
  Wallet,
  TrendingUp,
  User,
  LogOut,
  ArrowRight,
  AlertCircle,
  Copy,
  CheckCircle2,
  MessageCircle,
  ShieldCheck,
  Loader2,
  Lock,
  BanknoteIcon,
  Clock,
  History,
  Send,
  UserCog,
  Save,
  Phone,
  Mail,
  Building2,
  Globe,
  Users,
  X,
} from "lucide-react";
import Swal from "sweetalert2";

interface PartnerPortalProps {
  onLogout: () => void;
}

export const PartnerPortal: React.FC<PartnerPortalProps> = ({ onLogout }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<
    "dashboard" | "withdraw" | "profile"
  >("dashboard");

  // Data from API
  const [partnerData, setPartnerData] = useState<any>(null);
  const [withdrawals, setWithdrawals] = useState<Withdrawal[]>([]);
  const [balance, setBalance] = useState<WithdrawalBalance | null>(null);
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [withdrawNotes, setWithdrawNotes] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Profile state
  const [profile, setProfile] = useState<PartnerProfile | null>(null);
  const [profileForm, setProfileForm] = useState<UpdateProfilePayload>({});
  const [isSavingProfile, setIsSavingProfile] = useState(false);

  // Email verification state
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [emailStep, setEmailStep] = useState<"input" | "verify">("input");
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);
  const [isEmailLoading, setIsEmailLoading] = useState(false);

  useEffect(() => {
    const storedUser = getStoredUser();
    if (storedUser && storedUser.role === "partner") {
      setIsLoggedIn(true);
      fetchDashboard();
    }
  }, []);

  // Subscribe to realtime notification updates for partner
  useEffect(() => {
    const user = getStoredUser();
    if (!user || !isLoggedIn) return;

    const unsubscribe = subscribeToUserNotifications(
      user.id,
      (data: NotificationEventData) => {
        // Handle withdrawal status notifications
        if (data.type === "withdrawal_status") {
          // Refresh withdrawals and balance
          getPartnerWithdrawals().then(setWithdrawals);
          getPartnerBalance().then(setBalance);

          // Show notification
          const statusText =
            data.metadata?.status === "completed"
              ? "disetujui"
              : data.metadata?.status === "rejected"
              ? "ditolak"
              : "sedang diproses";

          Swal.fire({
            icon:
              data.metadata?.status === "completed"
                ? "success"
                : data.metadata?.status === "rejected"
                ? "error"
                : "info",
            title: data.title,
            text: data.message,
            timer: 5000,
            showConfirmButton: true,
          });
        }
      }
    );

    return () => unsubscribe();
  }, [isLoggedIn]);

  const fetchDashboard = async () => {
    setIsLoading(true);
    try {
      const [data, balanceData, withdrawalData, profileData] =
        await Promise.all([
          getPartnerDashboard(),
          getPartnerBalance(),
          getPartnerWithdrawals(),
          getPartnerProfile(),
        ]);
      setPartnerData(data);
      setBalance(balanceData);
      setWithdrawals(withdrawalData);
      setProfile(profileData);
      setProfileForm({
        fullName: profileData.fullName,
        phone: profileData.phone || "",
        socialMediaHandle: profileData.socialMediaHandle || undefined,
        socialMediaUrl: profileData.socialMediaUrl || "",
        followersCount: profileData.followersCount,
        bankType: profileData.bankType || undefined,
        bankName: profileData.bankName || "",
        bankAccountNumber: profileData.bankAccountNumber || "",
        bankAccountName: profileData.bankAccountName || "",
      });
    } catch (err) {
      console.error("Failed to fetch partner dashboard:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleWithdrawRequest = async () => {
    const amount = parseInt(withdrawAmount.replace(/\D/g, ""), 10);
    if (!amount || amount <= 0) {
      Swal.fire("Error", "Masukkan jumlah withdraw yang valid", "error");
      return;
    }

    if (balance && amount > balance.availableBalance) {
      Swal.fire(
        "Error",
        `Saldo tidak mencukupi. Saldo tersedia: Rp ${balance.availableBalance.toLocaleString(
          "id-ID"
        )}`,
        "error"
      );
      return;
    }

    const confirm = await Swal.fire({
      title: "Konfirmasi Withdraw",
      html: `
                <div class="text-left">
                    <p class="text-sm text-gray-600 mb-2">Anda akan mengajukan withdraw:</p>
                    <p class="text-2xl font-bold text-indigo-600">Rp ${amount.toLocaleString(
                      "id-ID"
                    )}</p>
                    <p class="text-xs text-gray-400 mt-2">Proses pencairan 1-3 hari kerja</p>
                </div>
            `,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Ya, Ajukan",
      cancelButtonText: "Batal",
      confirmButtonColor: "#4F46E5",
    });

    if (!confirm.isConfirmed) return;

    setIsSubmitting(true);
    try {
      await requestWithdrawal({ amount, notes: withdrawNotes || undefined });
      Swal.fire(
        "Berhasil!",
        "Pengajuan withdraw berhasil. Proses 1-3 hari kerja.",
        "success"
      );
      setWithdrawAmount("");
      setWithdrawNotes("");
      await fetchDashboard();
    } catch (err: any) {
      Swal.fire("Gagal", err?.message || "Gagal mengajukan withdraw", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveProfile = async () => {
    setIsSavingProfile(true);
    try {
      const response = await updatePartnerProfile(profileForm);
      Swal.fire("Berhasil!", response.message, "success");
      await fetchDashboard();
    } catch (err: any) {
      Swal.fire("Gagal", err?.message || "Gagal menyimpan profil", "error");
    } finally {
      setIsSavingProfile(false);
    }
  };

  const handleRequestEmailChange = async () => {
    if (!newEmail || !newEmail.includes("@")) {
      Swal.fire("Error", "Masukkan email yang valid", "error");
      return;
    }
    setIsEmailLoading(true);
    try {
      const response = await requestEmailChange(newEmail);
      setPendingEmail(response.pendingEmail);
      setEmailStep("verify");
      Swal.fire("Berhasil!", response.message, "success");
    } catch (err: any) {
      Swal.fire(
        "Gagal",
        err?.message || "Gagal mengirim kode verifikasi",
        "error"
      );
    } finally {
      setIsEmailLoading(false);
    }
  };

  const handleVerifyEmail = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      Swal.fire("Error", "Masukkan kode 6 digit", "error");
      return;
    }
    setIsEmailLoading(true);
    try {
      const response = await verifyEmailChange(verificationCode);
      Swal.fire("Berhasil!", response.message, "success");
      setShowEmailModal(false);
      setEmailStep("input");
      setNewEmail("");
      setVerificationCode("");
      setPendingEmail(null);
      await fetchDashboard();
    } catch (err: any) {
      Swal.fire(
        "Gagal",
        err?.message || "Kode verifikasi tidak valid",
        "error"
      );
    } finally {
      setIsEmailLoading(false);
    }
  };

  const handleCancelEmailChange = async () => {
    setIsEmailLoading(true);
    try {
      await cancelEmailChange();
      setShowEmailModal(false);
      setEmailStep("input");
      setNewEmail("");
      setVerificationCode("");
      setPendingEmail(null);
    } catch (err: any) {
      console.error(err);
    } finally {
      setIsEmailLoading(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      const response = await login(email, password);
      if (response.user.role !== "partner") {
        throw new Error("Hanya akun Mitra yang bisa masuk di sini.");
      }
      setIsLoggedIn(true);
      await fetchDashboard();
    } catch (err: any) {
      setError(
        err?.message || "Login gagal. Cek kembali email & password Anda."
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Simplified analytics - now using real API data
  const stats = partnerData?.stats;
  const history = partnerData?.history || [];
  const partner = partnerData?.partner;

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4 animate-fadeIn">
        <div className="bg-white p-8 rounded-3xl shadow-xl border border-gray-100 w-full max-w-md">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
              <TrendingUp className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Partner Portal</h2>
            <p className="text-gray-500 text-sm mt-1">
              Masuk untuk melihat komisi Anda
            </p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">
                Email
              </label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="email"
                  className="w-full pl-12 pr-4 py-4 border-2 border-gray-100 rounded-2xl bg-gray-50 text-gray-900 focus:bg-white focus:border-giz-primary outline-none transition-all font-bold"
                  placeholder="email@contoh.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="password"
                  className="w-full pl-12 pr-4 py-4 border-2 border-gray-100 rounded-2xl bg-gray-50 text-gray-900 focus:bg-white focus:border-giz-primary outline-none transition-all font-bold"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
            </div>

            {error && (
              <div className="flex items-center gap-2 text-red-500 text-xs bg-red-50 p-3 rounded-lg border border-red-100 font-bold">
                <AlertCircle className="w-4 h-4" /> {error}
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-slate-900 text-white font-black uppercase tracking-[0.2em] text-xs py-5 rounded-2xl shadow-xl hover:bg-black transition-all flex items-center justify-center gap-2 mt-4 disabled:opacity-50"
            >
              {isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                "MASUK DASHBOARD"
              )}
              {!isLoading && <ArrowRight className="w-4 h-4" />}
            </button>

            <button
              type="button"
              onClick={onLogout}
              className="w-full py-3 text-gray-400 font-bold hover:text-gray-600 transition-colors text-xs uppercase tracking-widest"
            >
              Kembali ke Beranda
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (isLoading && !partnerData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-indigo-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] animate-fadeIn font-sans">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center text-white font-black">
              P
            </div>
            <span className="font-black text-gray-800 text-sm tracking-tight uppercase">
              Partner <span className="text-indigo-600">Sync</span>
            </span>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right hidden sm:block">
              <p className="text-xs font-black text-gray-900 uppercase">
                {partner?.fullName}
              </p>
              <p className="text-[10px] text-gray-400 font-bold">
                {partner?.socialMediaHandle || "Mitra Surabaya Ticket"}
              </p>
            </div>
            <button
              onClick={() => {
                localStorage.removeItem("auth_token");
                localStorage.removeItem("auth_user");
                setIsLoggedIn(false);
                onLogout();
              }}
              className="text-gray-400 hover:text-red-600 flex items-center gap-2 text-xs font-black uppercase tracking-widest"
            >
              <LogOut className="w-4 h-4" /> Keluar
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab("dashboard")}
            className={`px-6 py-3 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${
              activeTab === "dashboard"
                ? "bg-indigo-600 text-white shadow-lg"
                : "bg-white text-gray-500 hover:bg-gray-50 border border-gray-200"
            }`}
          >
            <LayoutDashboard className="w-4 h-4 inline mr-2" /> Dashboard
          </button>
          <button
            onClick={() => setActiveTab("withdraw")}
            className={`px-6 py-3 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${
              activeTab === "withdraw"
                ? "bg-indigo-600 text-white shadow-lg"
                : "bg-white text-gray-500 hover:bg-gray-50 border border-gray-200"
            }`}
          >
            <BanknoteIcon className="w-4 h-4 inline mr-2" /> Withdraw
          </button>
          <button
            onClick={() => setActiveTab("profile")}
            className={`px-6 py-3 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${
              activeTab === "profile"
                ? "bg-indigo-600 text-white shadow-lg"
                : "bg-white text-gray-500 hover:bg-gray-50 border border-gray-200"
            }`}
          >
            <UserCog className="w-4 h-4 inline mr-2" /> Profil
          </button>
        </div>

        {activeTab === "dashboard" && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="bg-white p-7 rounded-[2.5rem] shadow-sm border border-gray-200 relative overflow-hidden group">
                <Wallet className="absolute -bottom-6 -right-6 w-32 h-32 text-indigo-500 opacity-5 group-hover:scale-110 transition-transform" />
                <p className="text-gray-400 text-[10px] font-black uppercase tracking-[0.2em] mb-1">
                  Saldo Tersedia
                </p>
                <h3 className="text-3xl font-black text-indigo-600">
                  Rp {balance?.availableBalance?.toLocaleString("id-ID") || "0"}
                </h3>
                <p className="text-[10px] text-gray-400 font-bold uppercase mt-3 bg-gray-50 w-fit px-3 py-1 rounded-full border border-gray-100">
                  Siap ditarik
                </p>
              </div>

              <div className="bg-white p-7 rounded-[2.5rem] shadow-sm border border-gray-200">
                <p className="text-gray-400 text-[10px] font-black uppercase tracking-[0.2em] mb-1">
                  Omset Penjualan (GMV)
                </p>
                <h3 className="text-3xl font-black text-gray-900">
                  Rp {stats?.totalSales?.toLocaleString("id-ID") || "0"}
                </h3>
                <div className="flex items-center gap-1 mt-3 text-emerald-600 text-[10px] font-black uppercase tracking-widest">
                  <TrendingUp className="w-3 h-3" /> Sales Aktif
                </div>
              </div>

              <div className="bg-white p-7 rounded-[2.5rem] shadow-sm border border-gray-200">
                <p className="text-gray-400 text-[10px] font-black uppercase tracking-[0.2em] mb-1">
                  Volume Konversi
                </p>
                <h3 className="text-3xl font-black text-gray-900">
                  {stats?.transactionCount || "0"}{" "}
                  <span className="text-sm font-bold opacity-30">BOOKINGS</span>
                </h3>
                <p className="text-[10px] text-gray-400 font-bold uppercase mt-3">
                  Rata-rata pax/transaksi
                </p>
              </div>
            </div>

            <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-7 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                <h3 className="font-black text-gray-900 uppercase tracking-widest text-sm">
                  Riwayat Komisi Multi-Tiket
                </h3>
                <div className="flex gap-2">
                  {stats?.voucherCodes?.map((code: string) => (
                    <div
                      key={code}
                      className="bg-white px-3 py-1.5 rounded-xl border border-indigo-100 text-indigo-600 font-mono font-black text-xs shadow-sm flex items-center gap-2"
                    >
                      {code} {" "}
                      <Copy
                        className="w-3 h-3 cursor-pointer opacity-40 hover:opacity-100"
                        onClick={() =>
                          navigator.clipboard.writeText(
                            `${code}`
                          )
                        }
                      />
                      <button
                        type="button"
                        className="bg-orange-50 hover:bg-orange-100 text-orange-600 rounded-lg px-2 py-1 text-[10px] font-black uppercase tracking-widest flex items-center gap-1"
                        style={{marginLeft: 2}}
                        onClick={async () => {
                          const link = `${window.location.origin}?voucher=${code}`;
                          if (navigator.share) {
                            try {
                              await navigator.share({
                                title: "Voucher Partner GIZ",
                                text: `Gunakan kode voucher berikut untuk diskon khusus: ${code}`,
                                url: link,
                              });
                            } catch (err) {}
                          } else {
                            navigator.clipboard.writeText(link);
                            window.alert("Link referral disalin ke clipboard!");
                          }
                        }}
                      >
                        Bagikan
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 8a3 3 0 11-6 0 3 3 0 016 0zm6 8a6 6 0 00-12 0v2a2 2 0 002 2h8a2 2 0 002-2v-2z" /></svg>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
              <div className="overflow-x-auto scrollbar-thin">
                <table className="w-full text-sm text-left border-separate border-spacing-0">
                  <thead className="bg-slate-50 text-gray-400 font-black uppercase text-[9px] tracking-[0.2em] border-b border-gray-100">
                    <tr>
                      <th className="px-8 py-5">Tanggal & Kode</th>
                      <th className="px-6 py-5">Wahana (Pax)</th>
                      <th className="px-6 py-5">Status Tiket</th>
                      <th className="px-6 py-5 text-right">Nilai Transaksi</th>
                      <th className="px-8 py-5 text-right text-indigo-600">
                        Komisi Mitra
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {history.length ? (
                      history.map((tx: any, idx: number) => (
                        <tr
                          key={idx}
                          className="hover:bg-gray-50/50 transition-colors"
                        >
                          <td className="px-8 py-5">
                            <div className="font-mono font-black text-gray-900 uppercase text-xs">
                              {tx.booking_code}
                            </div>
                            <div className="text-[10px] text-gray-400 font-bold uppercase mt-1">
                              {new Date(tx.visit_date).toLocaleDateString(
                                "id-ID",
                                {
                                  day: "numeric",
                                  month: "short",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                }
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-5">
                            <div className="font-black text-gray-800 text-xs uppercase truncate max-w-[180px]">
                              {tx.ticket_type}
                            </div>
                            <div className="text-[10px] text-indigo-500 font-black uppercase mt-1">
                              {tx.participant} PAX
                            </div>
                          </td>
                          <td className="px-6 py-5">
                            <span
                              className={`px-3 py-1 rounded-full text-[9px] font-black uppercase border ${
                                tx.payment_status === "paid"
                                  ? "bg-emerald-50 text-emerald-600 border-emerald-100"
                                  : tx.payment_status === "completed"
                                  ? "bg-blue-50 text-blue-600 border-blue-100"
                                  : "bg-amber-50 text-amber-600 border-amber-100"
                              }`}
                            >
                              {tx.payment_status}
                            </span>
                          </td>
                          <td className="px-6 py-5 text-right font-black text-gray-500 text-xs">
                            Rp {Number(tx.total_amount).toLocaleString("id-ID")}
                          </td>
                          <td className="px-8 py-5 text-right font-black text-indigo-600 text-base">
                            + Rp{" "}
                            {Number(tx.commission_snap).toLocaleString("id-ID")}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={5} className="px-8 py-24 text-center">
                          <MessageCircle className="w-12 h-12 text-gray-100 mx-auto mb-4" />
                          <p className="text-[10px] text-gray-300 font-black uppercase tracking-[0.4em]">
                            Belum Ada Transaksi
                          </p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div className="p-8 bg-indigo-600 text-white flex flex-col md:flex-row justify-between items-center gap-6">
                <div className="flex items-center gap-4">
                  <div className="bg-white/20 p-3 rounded-2xl border border-white/20 shadow-lg">
                    <ShieldCheck className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h4 className="font-black text-lg uppercase tracking-tighter leading-none">
                      Status Kemitraan Aktif
                    </h4>
                    <p className="text-[10px] text-indigo-100 font-bold uppercase tracking-widest mt-2">
                      {partner?.tier?.toUpperCase()} MEMBER | ID: {partner?.id}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-[10px] text-indigo-200 font-black uppercase tracking-widest mb-1">
                    Saldo Tersedia
                  </p>
                  <p className="text-3xl font-black">
                    Rp{" "}
                    {balance?.availableBalance?.toLocaleString("id-ID") || "0"}
                  </p>
                </div>
              </div>
            </div>
          </>
        )}

        {activeTab === "withdraw" && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Withdraw Form */}
            <div className="lg:col-span-1">
              <div className="bg-white p-7 rounded-[2.5rem] shadow-sm border border-gray-200">
                <h3 className="font-black text-gray-900 uppercase tracking-widest text-sm mb-6 flex items-center gap-2">
                  <Send className="w-4 h-4" /> Ajukan Withdraw
                </h3>

                <div className="space-y-4">
                  <div className="bg-indigo-50 p-4 rounded-2xl border border-indigo-100">
                    <p className="text-[10px] text-indigo-600 font-black uppercase tracking-widest mb-1">
                      Saldo Tersedia
                    </p>
                    <p className="text-2xl font-black text-indigo-600">
                      Rp{" "}
                      {balance?.availableBalance?.toLocaleString("id-ID") ||
                        "0"}
                    </p>
                  </div>

                  <div>
                    <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">
                      Jumlah Withdraw
                    </label>
                    <input
                      type="text"
                      className="w-full px-4 py-4 border-2 border-gray-100 rounded-2xl bg-gray-50 text-gray-900 focus:bg-white focus:border-indigo-500 outline-none transition-all font-bold text-lg"
                      placeholder="Rp 0"
                      value={withdrawAmount}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, "");
                        setWithdrawAmount(
                          val
                            ? `Rp ${parseInt(val).toLocaleString("id-ID")}`
                            : ""
                        );
                      }}
                    />
                  </div>

                  <div>
                    <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">
                      Catatan (Opsional)
                    </label>
                    <textarea
                      className="w-full px-4 py-3 border-2 border-gray-100 rounded-2xl bg-gray-50 text-gray-900 focus:bg-white focus:border-indigo-500 outline-none transition-all font-medium text-sm resize-none"
                      rows={3}
                      placeholder="Tambahkan catatan..."
                      value={withdrawNotes}
                      onChange={(e) => setWithdrawNotes(e.target.value)}
                    />
                  </div>

                  <div className="bg-amber-50 p-3 rounded-xl border border-amber-100">
                    <p className="text-[10px] text-amber-700 font-bold">
                      <Clock className="w-3 h-3 inline mr-1" />
                      Proses pencairan membutuhkan 1-3 hari kerja
                    </p>
                  </div>

                  <button
                    onClick={handleWithdrawRequest}
                    disabled={isSubmitting || !withdrawAmount}
                    className="w-full bg-indigo-600 text-white font-black uppercase tracking-[0.2em] text-xs py-4 rounded-2xl shadow-xl hover:bg-indigo-700 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {isSubmitting ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                    {isSubmitting ? "Memproses..." : "Ajukan Withdraw"}
                  </button>
                </div>
              </div>
            </div>

            {/* Withdrawal History */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-200 overflow-hidden">
                <div className="p-7 border-b border-gray-100 bg-gray-50/50">
                  <h3 className="font-black text-gray-900 uppercase tracking-widest text-sm flex items-center gap-2">
                    <History className="w-4 h-4" /> Riwayat Withdraw
                  </h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm text-left">
                    <thead className="bg-slate-50 text-gray-400 font-black uppercase text-[9px] tracking-[0.2em]">
                      <tr>
                        <th className="px-6 py-4">Tanggal</th>
                        <th className="px-6 py-4">Jumlah</th>
                        <th className="px-6 py-4">Rekening Tujuan</th>
                        <th className="px-6 py-4">Status</th>
                        <th className="px-6 py-4">Catatan</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {withdrawals.length ? (
                        withdrawals.map((w) => (
                          <tr key={w.id} className="hover:bg-gray-50/50">
                            <td className="px-6 py-4">
                              <div className="text-xs font-bold text-gray-900">
                                {new Date(w.createdAt).toLocaleDateString(
                                  "id-ID",
                                  {
                                    day: "numeric",
                                    month: "short",
                                    year: "numeric",
                                  }
                                )}
                              </div>
                              <div className="text-[10px] text-gray-400">
                                {new Date(w.createdAt).toLocaleTimeString(
                                  "id-ID",
                                  { hour: "2-digit", minute: "2-digit" }
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4 font-black text-indigo-600">
                              Rp {w.amount.toLocaleString("id-ID")}
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-xs font-bold text-gray-800">
                                {w.bankName}
                              </div>
                              <div className="text-[10px] text-gray-400 font-mono">
                                {w.bankAccountNumber}
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <span
                                className={`px-3 py-1 rounded-full text-[9px] font-black uppercase border ${
                                  w.status === "completed"
                                    ? "bg-emerald-50 text-emerald-600 border-emerald-100"
                                    : w.status === "pending"
                                    ? "bg-amber-50 text-amber-600 border-amber-100"
                                    : w.status === "processing"
                                    ? "bg-blue-50 text-blue-600 border-blue-100"
                                    : "bg-red-50 text-red-600 border-red-100"
                                }`}
                              >
                                {w.status === "completed"
                                  ? "Selesai"
                                  : w.status === "pending"
                                  ? "Menunggu"
                                  : w.status === "processing"
                                  ? "Diproses"
                                  : "Ditolak"}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              {w.adminNotes ? (
                                <div
                                  className={`text-[10px] p-2 rounded-lg max-w-[200px] ${
                                    w.status === "rejected"
                                      ? "bg-red-50 text-red-700 border border-red-100"
                                      : "bg-gray-50 text-gray-600"
                                  }`}
                                >
                                  <span className="font-bold block mb-0.5">
                                    {w.status === "rejected"
                                      ? "Alasan Penolakan:"
                                      : "Catatan Admin:"}
                                  </span>
                                  {w.adminNotes}
                                </div>
                              ) : w.notes ? (
                                <div className="text-[10px] text-gray-500 max-w-[200px]">
                                  <span className="font-bold block mb-0.5 text-gray-400">
                                    Catatan Anda:
                                  </span>
                                  {w.notes}
                                </div>
                              ) : (
                                <span className="text-[10px] text-gray-300">
                                  -
                                </span>
                              )}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={5} className="px-6 py-16 text-center">
                            <BanknoteIcon className="w-12 h-12 text-gray-100 mx-auto mb-4" />
                            <p className="text-[10px] text-gray-300 font-black uppercase tracking-[0.4em]">
                              Belum Ada Riwayat Withdraw
                            </p>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === "profile" && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Profile Info */}
            <div className="bg-white p-7 rounded-[2.5rem] shadow-sm border border-gray-200">
              <h3 className="font-black text-gray-900 uppercase tracking-widest text-sm mb-6 flex items-center gap-2">
                <User className="w-4 h-4" /> Informasi Akun
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">
                    <Mail className="w-3 h-3 inline mr-1" /> Email
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="email"
                      className="flex-1 px-4 py-3 border-2 border-gray-100 rounded-xl bg-gray-50 text-gray-500 font-bold cursor-not-allowed"
                      value={profile?.email || ""}
                      disabled
                    />
                    <button
                      onClick={() => setShowEmailModal(true)}
                      className="px-4 py-3 bg-indigo-100 text-indigo-600 rounded-xl font-bold text-xs hover:bg-indigo-200 transition-colors"
                    >
                      Ubah
                    </button>
                  </div>
                  <p className="text-[9px] text-gray-400 mt-1">
                    Klik "Ubah" untuk mengubah email dengan verifikasi
                  </p>
                </div>

                <div>
                  <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">
                    <User className="w-3 h-3 inline mr-1" /> Nama Lengkap
                  </label>
                  <input
                    type="text"
                    className="w-full px-4 py-3 border-2 border-gray-100 rounded-xl bg-gray-50 text-gray-900 focus:bg-white focus:border-indigo-500 outline-none transition-all font-bold"
                    value={profileForm.fullName || ""}
                    onChange={(e) =>
                      setProfileForm({
                        ...profileForm,
                        fullName: e.target.value,
                      })
                    }
                  />
                </div>

                <div>
                  <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">
                    <Phone className="w-3 h-3 inline mr-1" /> No. Telepon
                  </label>
                  <input
                    type="tel"
                    className="w-full px-4 py-3 border-2 border-gray-100 rounded-xl bg-gray-50 text-gray-900 focus:bg-white focus:border-indigo-500 outline-none transition-all font-bold"
                    placeholder="628xxxxxxxxxx"
                    value={profileForm.phone || ""}
                    onChange={(e) => {
                      let value = e.target.value.replace(/\D/g, "");
                      if (value.startsWith("0")) {
                        value = "62" + value.slice(1);
                      }
                      if (!value.startsWith("62") && value.length > 0) {
                        value = "62" + value;
                      }
                      setProfileForm({ ...profileForm, phone: value });
                    }}
                  />
                  <p className="text-[9px] text-gray-400 mt-1">
                    Format: 628xxxxxxxxxx (tanpa +)
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">
                      <Globe className="w-3 h-3 inline mr-1" /> Platform
                    </label>
                    <select
                      className="w-full px-4 py-3 border-2 border-gray-100 rounded-xl bg-gray-50 text-gray-900 focus:bg-white focus:border-indigo-500 outline-none transition-all font-bold"
                      value={profileForm.socialMediaHandle || ""}
                      onChange={(e) =>
                        setProfileForm({
                          ...profileForm,
                          socialMediaHandle: e.target.value as any,
                        })
                      }
                    >
                      <option value="">Pilih</option>
                      <option value="instagram">Instagram</option>
                      <option value="tiktok">TikTok</option>
                      <option value="youtube">YouTube</option>
                      <option value="blog">Blog</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">
                      <Users className="w-3 h-3 inline mr-1" /> Followers
                    </label>
                    <input
                      type="number"
                      className="w-full px-4 py-3 border-2 border-gray-100 rounded-xl bg-gray-50 text-gray-900 focus:bg-white focus:border-indigo-500 outline-none transition-all font-bold"
                      placeholder="0"
                      value={profileForm.followersCount || ""}
                      onChange={(e) =>
                        setProfileForm({
                          ...profileForm,
                          followersCount: parseInt(e.target.value) || 0,
                        })
                      }
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">
                    URL Sosial Media
                  </label>
                  <input
                    type="url"
                    className="w-full px-4 py-3 border-2 border-gray-100 rounded-xl bg-gray-50 text-gray-900 focus:bg-white focus:border-indigo-500 outline-none transition-all font-bold"
                    placeholder="https://instagram.com/username"
                    value={profileForm.socialMediaUrl || ""}
                    onChange={(e) =>
                      setProfileForm({
                        ...profileForm,
                        socialMediaUrl: e.target.value,
                      })
                    }
                  />
                </div>
              </div>
            </div>

            {/* Bank Info */}
            <div className="bg-white p-7 rounded-[2.5rem] shadow-sm border border-gray-200">
              <h3 className="font-black text-gray-900 uppercase tracking-widest text-sm mb-6 flex items-center gap-2">
                <Building2 className="w-4 h-4" /> Informasi Rekening
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">
                    Tipe Rekening
                  </label>
                  <select
                    className="w-full px-4 py-3 border-2 border-gray-100 rounded-xl bg-gray-50 text-gray-900 focus:bg-white focus:border-indigo-500 outline-none transition-all font-bold"
                    value={profileForm.bankType || ""}
                    onChange={(e) =>
                      setProfileForm({
                        ...profileForm,
                        bankType: e.target.value as any,
                      })
                    }
                  >
                    <option value="">Pilih Tipe</option>
                    <option value="bank">Bank</option>
                    <option value="e-wallet">E-Wallet</option>
                  </select>
                </div>

                <div>
                  <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">
                    Nama Bank / E-Wallet
                  </label>
                  <input
                    type="text"
                    className="w-full px-4 py-3 border-2 border-gray-100 rounded-xl bg-gray-50 text-gray-900 focus:bg-white focus:border-indigo-500 outline-none transition-all font-bold"
                    placeholder="BCA / Mandiri / Dana / GoPay"
                    value={profileForm.bankName || ""}
                    onChange={(e) =>
                      setProfileForm({
                        ...profileForm,
                        bankName: e.target.value,
                      })
                    }
                  />
                </div>

                <div>
                  <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">
                    Nomor Rekening
                  </label>
                  <input
                    type="text"
                    className="w-full px-4 py-3 border-2 border-gray-100 rounded-xl bg-gray-50 text-gray-900 focus:bg-white focus:border-indigo-500 outline-none transition-all font-bold font-mono"
                    placeholder="1234567890"
                    value={profileForm.bankAccountNumber || ""}
                    onChange={(e) =>
                      setProfileForm({
                        ...profileForm,
                        bankAccountNumber: e.target.value,
                      })
                    }
                  />
                </div>

                <div>
                  <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">
                    Nama Pemilik Rekening
                  </label>
                  <input
                    type="text"
                    className="w-full px-4 py-3 border-2 border-gray-100 rounded-xl bg-gray-50 text-gray-900 focus:bg-white focus:border-indigo-500 outline-none transition-all font-bold uppercase"
                    placeholder="NAMA SESUAI REKENING"
                    value={profileForm.bankAccountName || ""}
                    onChange={(e) =>
                      setProfileForm({
                        ...profileForm,
                        bankAccountName: e.target.value,
                      })
                    }
                  />
                </div>

                <div className="bg-amber-50 p-3 rounded-xl border border-amber-100 mt-4">
                  <p className="text-[10px] text-amber-700 font-bold">
                    <AlertCircle className="w-3 h-3 inline mr-1" />
                    Pastikan data rekening sudah benar untuk proses withdraw
                  </p>
                </div>

                <button
                  onClick={handleSaveProfile}
                  disabled={isSavingProfile}
                  className="w-full bg-indigo-600 text-white font-black uppercase tracking-[0.2em] text-xs py-4 rounded-2xl shadow-xl hover:bg-indigo-700 transition-all flex items-center justify-center gap-2 mt-4 disabled:opacity-50"
                >
                  {isSavingProfile ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  {isSavingProfile ? "Menyimpan..." : "Simpan Perubahan"}
                </button>
              </div>
            </div>

            {/* Status Card */}
            <div className="lg:col-span-2 bg-indigo-600 p-8 rounded-[2.5rem] text-white">
              <div className="flex flex-col md:flex-row justify-between items-center gap-6">
                <div className="flex items-center gap-4">
                  <div className="bg-white/20 p-3 rounded-2xl border border-white/20 shadow-lg">
                    <ShieldCheck className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h4 className="font-black text-lg uppercase tracking-tighter leading-none">
                      Status:{" "}
                      {profile?.status === "active" ? "Aktif" : "Tidak Aktif"}
                    </h4>
                    <p className="text-[10px] text-indigo-100 font-bold uppercase tracking-widest mt-2">
                      Tier: {profile?.tier} | Bergabung:{" "}
                      {profile?.createdAt
                        ? new Date(profile.createdAt).toLocaleDateString(
                            "id-ID",
                            {
                              day: "numeric",
                              month: "long",
                              year: "numeric",
                            }
                          )
                        : "-"}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-[10px] text-indigo-200 font-black uppercase tracking-widest mb-1">
                    ID Partner
                  </p>
                  <p className="text-xl font-black font-mono">
                    {profile?.id?.slice(0, 8).toUpperCase()}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Email Verification Modal */}
      {showEmailModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn">
          <div className="bg-white rounded-3xl w-full max-w-md shadow-2xl overflow-hidden">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50">
              <div>
                <h3 className="font-black text-gray-900 uppercase tracking-widest text-sm">
                  {emailStep === "input" ? "Ubah Email" : "Verifikasi Email"}
                </h3>
                <p className="text-xs text-gray-400 mt-1">
                  {emailStep === "input"
                    ? "Masukkan email baru Anda"
                    : `Kode dikirim ke ${pendingEmail}`}
                </p>
              </div>
              <button
                onClick={() => {
                  if (emailStep === "verify") {
                    handleCancelEmailChange();
                  } else {
                    setShowEmailModal(false);
                    setNewEmail("");
                  }
                }}
                className="p-2 hover:bg-gray-200 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            <div className="p-6">
              {emailStep === "input" ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">
                      Email Saat Ini
                    </label>
                    <input
                      type="email"
                      className="w-full px-4 py-3 border-2 border-gray-100 rounded-xl bg-gray-50 text-gray-500 font-bold cursor-not-allowed"
                      value={profile?.email || ""}
                      disabled
                    />
                  </div>

                  <div>
                    <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">
                      Email Baru
                    </label>
                    <input
                      type="email"
                      className="w-full px-4 py-3 border-2 border-gray-100 rounded-xl bg-gray-50 text-gray-900 focus:bg-white focus:border-indigo-500 outline-none transition-all font-bold"
                      placeholder="email.baru@contoh.com"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                    />
                  </div>

                  <div className="bg-amber-50 p-3 rounded-xl border border-amber-100">
                    <p className="text-[10px] text-amber-700 font-bold">
                      <AlertCircle className="w-3 h-3 inline mr-1" />
                      Kode verifikasi akan dikirim ke email baru
                    </p>
                  </div>

                  <button
                    onClick={handleRequestEmailChange}
                    disabled={isEmailLoading || !newEmail}
                    className="w-full bg-indigo-600 text-white font-black uppercase tracking-[0.2em] text-xs py-4 rounded-2xl shadow-xl hover:bg-indigo-700 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {isEmailLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                    {isEmailLoading ? "Mengirim..." : "Kirim Kode Verifikasi"}
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Mail className="w-8 h-8 text-indigo-600" />
                    </div>
                    <p className="text-sm text-gray-600">
                      Masukkan kode 6 digit yang dikirim ke:
                    </p>
                    <p className="font-bold text-indigo-600">{pendingEmail}</p>
                  </div>

                  <div>
                    <input
                      type="text"
                      maxLength={6}
                      className="w-full px-4 py-4 border-2 border-gray-100 rounded-xl bg-gray-50 text-gray-900 focus:bg-white focus:border-indigo-500 outline-none transition-all font-black text-2xl text-center tracking-[0.5em]"
                      placeholder="000000"
                      value={verificationCode}
                      onChange={(e) =>
                        setVerificationCode(
                          e.target.value.replace(/\D/g, "").slice(0, 6)
                        )
                      }
                    />
                  </div>

                  <div className="bg-gray-50 p-3 rounded-xl border border-gray-100">
                    <p className="text-[10px] text-gray-500 font-bold text-center">
                      Kode akan kadaluarsa dalam 15 menit
                    </p>
                  </div>

                  <button
                    onClick={handleVerifyEmail}
                    disabled={isEmailLoading || verificationCode.length !== 6}
                    className="w-full bg-indigo-600 text-white font-black uppercase tracking-[0.2em] text-xs py-4 rounded-2xl shadow-xl hover:bg-indigo-700 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {isEmailLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <CheckCircle2 className="w-4 h-4" />
                    )}
                    {isEmailLoading ? "Memverifikasi..." : "Verifikasi Email"}
                  </button>

                  <button
                    onClick={handleCancelEmailChange}
                    className="w-full py-3 text-gray-400 font-bold hover:text-gray-600 transition-colors text-xs uppercase tracking-widest"
                  >
                    Batalkan Perubahan
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
